<template>
  <v-container class="py-12 py-md-8 px-5 px-md-16">
    <v-card-title
      class="display-1 font-weight-bold pa-0 mb-3 letter-spacing-normal"
    >
      {{ species.name.capitalize() }} ({{ species.family.name }})
    </v-card-title>
    <v-card-subtitle class="title pa-0 d-block mb-8">
      {{ species.latin_name }}
    </v-card-subtitle>
    <v-row class="mb-8">
      <v-list-item>
        <v-list-item-content>
          <v-list-item-title class="font-weight-bold">
            Recommended Cost
          </v-list-item-title>
        </v-list-item-content>
        <v-list-item-content>
          <v-list-item-title class="notranslate">
            ${{ species.recommended_cost }}
          </v-list-item-title>
        </v-list-item-content>
      </v-list-item>
      <v-list-item v-if="showCustomCost">
        <v-list-item-content>
          <v-list-item-title class="font-weight-bold">
            Set Custom Cost:
          </v-list-item-title>
        </v-list-item-content>
        <v-list-item-content>
          <v-text-field
            type="number"
            v-model="customCost"
            ref="customCostInput"
            autofocus
          ></v-text-field>
        </v-list-item-content>
      </v-list-item>
    </v-row>
    <v-divider />
    <v-row align="center" class="flex-column flex-md-row my-8">
      <v-col cols="md-3">
        <div class="font-weight-bold">Filter data</div>
      </v-col>
      <v-col cols="md-5">
        <div id="tourSelect" class="position-relative">
          <v-select
            :menu-props="{ contentClass: 'notranslate' }"
            hide-details
            single-line
            attach="#tourSelect"
            :items="tours"
            v-model="filters.tour_id"
            class="body-2 black--text font-weight-bold"
            height="40"
            label="Tours"
            item-text="name"
            item-value="id"
            solo
          />
        </div>
      </v-col>
      <v-col cols="md-4">
        <div id="yearSelect" class="position-relative">
          <v-select
            :menu-props="{ contentClass: 'notranslate' }"
            hide-details
            single-line
            attach="#yearSelect"
            :items="
              new Array(10)
                .fill(parseInt($moment().format('YYYY')))
                .map((v, i) => {
                  return { text: v - i, value: v - i };
                })
            "
            v-model="filters.year"
            class="body-2 black--text font-weight-bold"
            height="40"
            label="Year"
            solo
          />
        </div>
      </v-col>
    </v-row>

    <v-row>
      <v-list-item>
        <v-list-item-content>
          <v-list-item-title class="font-weight-bold">
            Chance of Sighting
          </v-list-item-title>
        </v-list-item-content>
        <v-list-item-content>
          <v-list-item-title>
            {{ species.sighting_chance }}
          </v-list-item-title>
        </v-list-item-content>
      </v-list-item>
    </v-row>

    <LineChart
      v-if="showChart"
      :data-sets="dataSets"
      :labels="labels"
      label="Species Data"
      class="mt-10"
    />

    <v-row
      class="flex-column flex-md-row justify-lg-space-between align-center mt-16"
    >
      <v-col class="pa-0">
        <v-data-table
          :headers="headers"
          :items="speciesPriceData"
          :hide-default-footer="true"
          class="border-radius-0 v-data-table-1 v-data-table-1--style3"
        >
          <template v-slot:body.append>
            <tr class="table-footer">
              <td colspan="3" />
              <td>
                ${{
                  speciesPriceData
                    .reduce((total, data) => total + data.total.currency(), 0)
                    .currency(2)
                }}
              </td>
            </tr>
          </template>
        </v-data-table>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import Species from '@models/Species';
import Tour from '@models/Tour';

export default {
  name: 'SpeciesStatistics',
  components: { LineChart: () => import('@components/charts/LineChart') },
  props: {
    showCustomCost: {
      type: Boolean,
    },
    species: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      customCost: '',
      filters: {
        year: 2020,
        tour_id: '',
      },
      tours: [
        {
          id: null,
          name: 'All Tours',
        },
      ],
      model: new Species({
        filters: {
          ...this.filters,
        },
      }),
      total: 0,
      headers: [
        { text: 'Date', value: 'date', sortable: false },
        { text: 'Count', value: 'count', sortable: false },
        { text: '~ Price', value: 'price', sortable: false },
        { text: 'Earnings', value: 'total', sortable: false },
      ],
      speciesPriceData: [],
      labels: [
        'Jan',
        'Feb',
        'Mar',
        'Apr',
        'May',
        'Jun',
        'Jul',
        'Aug',
        'Sep',
        'Oct',
        'Nov',
        'Dec',
      ],
      dataSets: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
      showChart: true,
    };
  },
  watch: {
    customCost: {
      handler(value) {
        const cost = Number(value);

        if (cost > 0) {
          this.$emit('speciesCostChange', { [this.species.id]: cost });
        }
      },
    },
    async dataSets() {
      this.showChart = false;
      await this.$nextTick();
      this.showChart = true;
    },
    filters: {
      handler() {
        this.fetchStats();
      },
      deep: true,
    },
  },
  methods: {
    async fetchTours() {
      let response = await new Tour({
        itemsPerPage: -1,
      }).get();
      if (response) {
        this.tours.push(...response.data);
      }
    },
    async fetchStats() {
      let self = this;
      self.model.setFilters(self.filters);
      let response = await self.model.fetchStats(self.species.id);
      if (response) {
        let data = response.data;
        this.speciesPriceData = data;
        let dataSets = new Array(12).fill(0);
        if (data.length) {
          let months = self.labels;
          data.forEach((stat) => {
            let index = months.findIndex(
              (month) => stat.date.excerpt(3, '') === month
            );
            if (index > -1) {
              dataSets[index] = stat.count;
            }
          });
          await self.$nextTick();
        }
        self.dataSets = dataSets;
        this.total = response.meta.total;
      }
    },
  },
  async mounted() {
    this.fetchStats();
    this.fetchTours();

    if (this.species.has_custom_cost === true) {
      this.customCost = this.species.cost;
    }
  },
};
</script>

<style lang="scss">
.custom-select {
  .v-select {
    ~ .v-menu__content {
      top: 40px !important;
    }
  }
}
</style>
